import { createContext, useContext, useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, obj=logged in
  const [error, setError] = useState("");

  const bootstrap = async () => {
    // handle google redirect (session_id in hash)
    const hash = window.location.hash;
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        setUser(data.user);
        window.history.replaceState({}, "", window.location.pathname);
        return;
      } catch (e) {
        setError(getErrorMessage(e));
      }
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      // On transient server errors (5xx) or network hiccups, retry once before
      // deciding the user is logged out — avoids kicking valid sessions on reload.
      const status = e?.response?.status;
      if (localStorage.getItem("aos_token") && (!e?.response || (status >= 500))) {
        await new Promise((r) => setTimeout(r, 1500));
        try {
          const { data } = await api.get("/auth/me");
          setUser(data.user);
          return;
        } catch { /* fall through to logged-out */ }
      }
      setUser(false);
    }
  };

  useEffect(() => { bootstrap(); }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("aos_token", data.access_token);
      setUser(data.user);
      return true;
    } catch (e) {
      setError(getErrorMessage(e));
      return false;
    }
  };

  const recoverAdmin = async (token) => {
    const { data } = await api.post("/auth/recover-admin", { token });
    return data;
  };

  const loginGoogle = () => {
    const redirect = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("aos_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, loginGoogle, logout, recoverAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}
